import { useState } from 'react'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID, useElitePlansLanding } from '../ElitePlansLandingContext'
import { buildElitePlansCtaPayload } from '../elitePlansLandingState'
import { resolveElitePrimeUpgradeCtaFeeAmount, resolveRecommendedElitePrimePlan } from '../elitePlansPrimaryCtaDisplay'
import {
  buildElitePlansUpgradeCtaPayload,
  resolveElitePrimeUpgradeDetails,
  type ElitePrimeUpgradeDetails
} from '../elitePlansUpgradeDetails'
import { ELITE_PRIME_UPGRADE_CTA_LABEL } from '../constants/ctaCopy'
import { resolvePlanId } from '../../new-elite-landing/components/NewEliteLandingPlanSelector'
import type { EliteOnboardingEligiblePlan } from '../../../types/misc.types'
import { buildFamilyOnboardingViewProps } from '../../new-elite-landing/pages/family/familyOnboardingViewProps'
import { FamilyFAQs } from '../../../pages/elite-landing-pages/family/FamilyFAQs'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
import { postCTA as postCta } from '../../../utils/appBridge'
import { grantElitePrimeConsent } from '../../../repository/misc'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import LeftLeafSVG from '../../../icons/LeftLeafSVG'
import RightLeafSVG from '../../../icons/RightLeafSVG'
import VaulDrawer from '../../../components/BottomSheetComponents/VaulDrawer'
import { KYCCompletionBottomSheet } from '../../../components/BottomSheetComponents/elite-family/KYCCompletionBottomSheet'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../state-management/contexts/ToastContext'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import { ElitePlansPrimaryCta } from './hero/ctas/ElitePlansPrimaryCta'
import { ElitePlansConsentRow } from './hero/ElitePlansConsentRow'
import { ElitePlansSupportText } from './hero/ElitePlansSupportText'
import {
  ElitePlansPricingDetailsBottomSheet,
  elitePlansPricingDetailsTitleId
} from './ElitePlansPricingDetailsBottomSheet'

const FAMILY_ELITE_COPY = {
  planName: 'Elite',
  priceLead: 'at just',
  description:
    'A special price, just for your loved ones. Fund selection, review to rebalancing - now make their portfolios strong like yours, with Elite.',
  aboutPath: '/elite/v5?pageType=no-upgrade',
  backgroundSrc: '/images/elite-plans/elite_bg.svg',
  wordmarkGradientClass: 'bg-gradient-to-r from-elite50 to-elite30'
}

const FAMILY_PRIME_COPY = {
  planName: 'Prime',
  priceLead: 'at',
  description:
    'A special price, just for your loved ones. Fund selection, review to rebalancing - now make their portfolios strong like yours, with Prime.',
  aboutPath: '/elite/v5?pageType=no-upgrade',
  backgroundSrc: '/images/elite-plans/prime_bg.svg',
  wordmarkGradientClass: 'bg-gradient-to-r from-prime50 to-prime30'
}

/**
 * Family purchase screen: primary user is Elite / Prime and is buying the plan for a free secondary user.
 *
 * Belongs here: family wordmark, price, FAQs, consent + Continue footer (used on `/elite-plans-family-landing`).
 * Does not belong here: blocked family state (hero CTA) or the regular sell sections.
 */
export function ElitePlansFamilyPurchaseBody() {
  const { activeTab, status, lifecycle } = useElitePlansLanding()
  const [isConsentChecked, setIsConsentChecked] = useState(true)
  const [isPricingDetailsOpen, setIsPricingDetailsOpen] = useState(false)
  const [isGrantingPrimeConsent, setIsGrantingPrimeConsent] = useState(false)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { showToast } = useToast()
  const isElitePrimeTab = activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID
  const copy = isElitePrimeTab ? FAMILY_PRIME_COPY : FAMILY_ELITE_COPY
  const familyConfig = buildFamilyOnboardingViewProps(status)
  const secondaryUserName = familyConfig.secondaryUserName
  // Blocked primary should use sell hero, but keep Continue disabled if this body is still mounted
  const isFamilyBlocked = familyConfig.isEliteBlockedBecausePrimaryNotElite
  const familyPlan = resolveRecommendedElitePrimePlan(status)
  // Family member already on Elite: Prime tab switches to the upgrade price + upgrade CTA (same gate as the hero)
  const elitePrimeUpgrade = isElitePrimeTab ? resolveElitePrimeUpgradeDetails(status) : null
  const upgradePricing = elitePrimeUpgrade?.upgradePricingDetails
  // Upgrade price + struck market price match the hero CTA (`resolveElitePlansPrimaryCtaDisplay`)
  const feeAmount = upgradePricing
    ? resolveElitePrimeUpgradeCtaFeeAmount(familyPlan?.effectivePlanFees?.amount, upgradePricing)
    : (familyPlan?.effectivePlanFees?.amount ?? 0)
  const listFeeAmount = upgradePricing
    ? (status?.marketPricingDetails?.marketPrice?.amount ?? 0)
    : (familyPlan?.basePlanFees?.amount ?? 0)
  const analyticsPlanId = resolveFamilyAnalyticsPlanId(elitePrimeUpgrade, familyPlan, activeTab.id)
  const pricingDetailsTemplate = activeTab.plusBanner?.pricingDetails
  const isCtaDisabled =
    isFamilyBlocked ||
    lifecycle.isCtaDisabledByState ||
    (lifecycle.showConsent && !isConsentChecked) ||
    isGrantingPrimeConsent

  const postContinueCta = () => {
    if (isFamilyBlocked) return
    const ctaPayload = elitePrimeUpgrade
      ? buildElitePlansUpgradeCtaPayload(elitePrimeUpgrade)
      : buildElitePlansCtaPayload(status, lifecycle.liveCta)
    if (!ctaPayload) return
    postCta(JSON.stringify(ctaPayload))
  }

  /** Prime advisory consent must be recorded before CTA (same as ElitePlansHero). */
  const grantPrimeConsentIfNeeded = async (): Promise<boolean> => {
    if (!isElitePrimeTab || !status?.isPrimeConsent) return true
    setIsGrantingPrimeConsent(true)
    try {
      await grantElitePrimeConsent()
      return true
    } catch {
      showToast({ message: 'Something went wrong. Please try again.', duration: 2500 })
      return false
    } finally {
      setIsGrantingPrimeConsent(false)
    }
  }

  // Family member's KYC consent must be authorised on this phone before the purchase CTA fires
  const handleContinue = async () => {
    const primeConsentOk = await grantPrimeConsentIfNeeded()
    if (!primeConsentOk) return

    if (status?.consentText) {
      openBottomSheet(
        <KYCCompletionBottomSheet
          familyMemberName={secondaryUserName}
          consentText={status?.consentText ?? ''}
          onDismiss={closeBottomSheet}
          onAuthorize={() => {
            postContinueCta()
            closeBottomSheet()
          }}
        />
      )
      return
    }
    postContinueCta()
  }

  const openPricingDetails = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId: analyticsPlanId,
      buttonLabel: 'View Pricing details'
    })
    setIsPricingDetailsOpen(true)
  }

  return (
    // Same scroll model as the standalone family page: content scrolls, footer stays in flow below it
    <div className='flex min-h-0 flex-1 flex-col bg-[color:var(--ep-below-hero)] text-center'>
      <div className='no-scrollbar relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none'>
        <img
          src={copy.backgroundSrc}
          alt=''
          aria-hidden='true'
          className='pointer-events-none absolute inset-x-0 top-[-100px] w-full select-none'
        />
        <div className='relative flex flex-col items-center px-5 pb-6 pt-28'>
          <p
            aria-label={copy.planName}
            className='flex items-center gap-1 text-[44px] font-medium leading-none text-[color:var(--ep-accent)]'
          >
            <LeftLeafSVG width={30} height={32} color='currentColor' />
            <span aria-hidden='true' className={`bg-clip-text text-transparent ${copy.wordmarkGradientClass}`}>
              {copy.planName}
            </span>
            <RightLeafSVG width={30} height={32} color='currentColor' />
          </p>
          <h1 className='mt-12 text-base font-medium leading-[1.2] text-[#4D3E38]'>
            Get {copy.planName}
            {secondaryUserName ? ` for ${secondaryUserName}` : ''} {copy.priceLead}
          </h1>
          <p className='mt-2 flex items-baseline gap-2'>
            <span className='text-2xl font-bold tracking-[-1px] text-[color:var(--ep-accent)]'>
              {roundOffAndFormatNumberWithCurrency(feeAmount)}/yr
              <span className='ml-1 text-xs font-normal tracking-normal'>+GST</span>
            </span>
            {/* Prime shows only the payable price (no struck list / market price) */}
            {!isElitePrimeTab && listFeeAmount > feeAmount ? (
              <span className='text-[22px] text-[#8E9393] line-through'>
                {roundOffAndFormatNumberWithCurrency(listFeeAmount)}/yr
              </span>
            ) : null}
          </p>
          {pricingDetailsTemplate ? (
            <button
              type='button'
              onClick={openPricingDetails}
              className='ep-focus-ring mt-2 inline-flex min-h-11 items-center gap-1 border-none bg-transparent text-13 font-medium text-[#6A6E6E]'
            >
              View Pricing details
              <ChevronRightIcon size={10} color='#6A6E6E' />
            </button>
          ) : null}
          {copy.description ? (
            <p className='mt-3 max-w-[320px] text-13 leading-[1.6] text-[#6A6E6E]'>{copy.description}</p>
          ) : null}

          <hr className='mt-8 w-full border-[#EBF2F2]' />
          <div className='w-full text-left'>
            <h2 className='pb-3 pt-8 text-13 font-normal text-[#8E9393]'>FAQs</h2>
            <FamilyFAQs />
          </div>

          {/* <button
            type='button'
            onClick={() => navigate(copy.aboutPath)}
            className='ep-focus-ring mt-6 flex min-h-12 w-full items-center justify-between rounded-xl border border-[#EBF2F2] bg-white p-4 text-13 font-medium text-black'
          >
            About {copy.planName}
            <ChevronRightIcon size={10} color='#000000' />
          </button> */}
        </div>
      </div>

      <div
        className='shrink-0 border-t border-[#EBF2F2] bg-[color:var(--ep-below-hero)] px-5 pt-3'
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 12px)` }}
      >
        {lifecycle.supportText ? (
          <ElitePlansSupportText supportText={lifecycle.supportText} planId={activeTab.id} />
        ) : null}
        {lifecycle.showConsent ? (
          <ElitePlansConsentRow
            planId={activeTab.id}
            consentTextList={lifecycle.consentTextList}
            checked={isConsentChecked}
            onCheckedChange={setIsConsentChecked}
          />
        ) : null}
        <ElitePlansPrimaryCta
          display={{ variant: 'plain', label: elitePrimeUpgrade ? ELITE_PRIME_UPGRADE_CTA_LABEL : 'Continue' }}
          planId={analyticsPlanId}
          disabled={isCtaDisabled}
          landingPageState={lifecycle.landingPageState}
          eventExtras={getEliteBuyCtaEventExtras(status)}
          onActivate={handleContinue}
          className='mt-4'
        />
      </div>

      {pricingDetailsTemplate ? (
        <VaulDrawer
          open={isPricingDetailsOpen}
          setOpen={setIsPricingDetailsOpen}
          titleId={elitePlansPricingDetailsTitleId}
        >
          <ElitePlansPricingDetailsBottomSheet
            open={isPricingDetailsOpen}
            template={pricingDetailsTemplate}
            onClose={() => setIsPricingDetailsOpen(false)}
          />
        </VaulDrawer>
      ) : null}
    </div>
  )
}

// Analytics `planId` follows the CTA source (upgrade target vs recommended family plan), like the hero
function resolveFamilyAnalyticsPlanId(
  elitePrimeUpgrade: ElitePrimeUpgradeDetails | null,
  familyPlan: EliteOnboardingEligiblePlan | undefined,
  tabId: string
): string {
  const upgradePlanId =
    elitePrimeUpgrade?.upgradePlanDetails?.effectivePlanId ?? elitePrimeUpgrade?.upgradePlanDetails?.basePlanId
  if (upgradePlanId) return upgradePlanId
  return resolvePlanId(familyPlan) || tabId
}
